import { API_BASE, ENDPOINTS, getCsrfHeaders } from './transport';

const authUrl = (endpoint) => `${API_BASE}/${endpoint}/`;
const VALID_STATES = new Set(['DISABLED', 'INTERNAL', 'BETA', 'ACTIVE']);

export async function getClerkStatus() {
    const response = await fetch(authUrl(ENDPOINTS.AUTH.CLERK_STATUS), {
        credentials: 'include',
        cache: 'no-store'
    });

    if (!response.ok) {
        throw new Error(`Clerk status request failed with HTTP ${response.status}.`);
    }

    const data = await response.json();
    if (!data || !VALID_STATES.has(data.state) || typeof data.configured !== 'boolean' || typeof data.outage !== 'boolean') {
        throw new Error('Clerk status response is missing state, configured, or outage.');
    }
    return data;
}

/**
 * Build the bridge request body for at most one admission credential.
 *
 * Returns `null` when neither credential is supplied, so the caller can omit the
 * body ENTIRELY. That is load-bearing: the automatic first attempt for a mapped,
 * returning user must stay byte-identical to the original body-less probe.
 *
 * When both are present the token wins and the raw key is NOT also sent. The
 * backend would ignore the key anyway (token takes precedence, and a dead token is
 * a hard failure that never downgrades to the key), so including it would only put
 * a durable, reusable credential on the wire for no effect.
 */
function buildAdmissionBody(betaKey, admissionToken) {
    if (admissionToken !== undefined) return { admission_token: admissionToken };
    if (betaKey !== undefined) return { beta_key: betaKey };
    return null;
}

/**
 * Exchange a completed Clerk session for a Django session.
 *
 * Two mutually exclusive admission credentials are accepted, both optional:
 *
 * - `admissionToken` — the short-lived, single-use carrier minted by the read-only
 *   beta-key precheck (`validate-beta-key`). This is what survives the Clerk OAuth
 *   redirect, so a user who already proved their invite is not asked again. It is
 *   worthless without this very request's verified Clerk session.
 * - `betaKey` — the raw key. RETAINED, not superseded: it is what
 *   `ClerkBetaAdmissionScreen` submits on an explicit user-driven retry, and what
 *   the QR / shared-invite prefill path uses. Do not remove it.
 *
 * The token takes precedence when both are given (see `buildAdmissionBody`), which
 * matches the backend: a supplied-but-dead token is a hard `beta_admission_token_invalid`
 * failure and never silently falls back to the raw key.
 *
 * Neither credential is ever logged, cached, or persisted by this module. Key
 * validity, `INDIVIDUAL` target-email matching, token expiry/single-use, and atomic
 * `SHARED` consumption are all decided server-side under lock; the frontend must
 * not pre-check, duplicate, or second-guess that decision.
 */
export async function bridgeClerkSession({ token, betaKey, admissionToken } = {}) {
    if (typeof token !== 'string' || !token.trim()) {
        throw new Error('A completed Clerk session token is required for the bridge.');
    }
    if (betaKey !== undefined && (typeof betaKey !== 'string' || !betaKey.trim())) {
        throw new Error('A beta key, when supplied to the Clerk bridge, must be a non-empty string.');
    }
    if (admissionToken !== undefined && (typeof admissionToken !== 'string' || !admissionToken.trim())) {
        throw new Error('An admission token, when supplied to the Clerk bridge, must be a non-empty string.');
    }

    const body = buildAdmissionBody(betaKey, admissionToken);

    const response = await fetch(authUrl(ENDPOINTS.AUTH.CLERK_BRIDGE), {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
            Authorization: `Bearer ${token}`
        },
        // Omit the body entirely when no admission credential is supplied so the
        // initial mapped-user probe stays byte-identical to the pre-F2 request.
        ...(body === null ? {} : { body: JSON.stringify(body) })
    });

    // A gateway/proxy failure (HTML 502, empty body) makes json() throw a bare
    // SyntaxError carrying no status. Convert it into a typed transport error so
    // the recovery mapping can render a real state instead of treating an
    // infrastructure outage as an unrecognized application error.
    let data;
    try {
        data = await response.json();
    } catch (parseError) {
        const error = new Error(
            `Clerk bridge returned a non-JSON response (HTTP ${response.status}): ${parseError.message}`
        );
        error.status = response.status;
        error.errorType = 'invalid_response';
        throw error;
    }

    if (!response.ok) {
        const error = new Error(`Clerk bridge failed with HTTP ${response.status}.`);
        error.status = response.status;
        // Throttling is enforced by DRF, which answers with `{"detail": ...}` and no
        // `error_type`. Derive it from the status so 429 gets its own retry-later
        // state rather than collapsing into the unknown-error bucket.
        if (data?.error_type) {
            error.errorType = data.error_type;
        } else if (response.status === 429) {
            error.errorType = 'throttled';
        } else {
            error.errorType = 'unknown';
        }
        throw error;
    }
    if (data?.status !== 'success' || !data.user) {
        const error = new Error('Clerk bridge returned an invalid success response.');
        error.status = response.status;
        error.errorType = 'invalid_response';
        throw error;
    }
    return data;
}
